<script lang="ts">
import {
    formatBuilderImageSrcsetForAspectRatio,
    formatBuilderImageUrlForAspectRatio,
} from '$lib/utils/formatImageUrl.js';
import { getLinkAttributes, stripSiteDomain } from '$lib/utils/url.js';
import BlockHeader from '../BlockHeader/BlockHeader.svelte';

const FEATURED_IMAGE_SIZES =
    '(max-width: 767px) calc(100vw - 32px), (max-width: 1199px) calc(100vw - 48px), calc((100vw - 148px) / 2)';
const FEATURED_IMAGE_PARAMS = 'format=webp&quality=75';
const FEATURED_IMAGE_ASPECT_RATIO = 16 / 9;
const FEATURED_IMAGE_WIDTHS = [480, 640, 800, 960, 1200, 1600] as const;
const FEATURED_IMAGE_FALLBACK_WIDTH = 1200;

let { features = [], header = '', ctaTo = '', ctaText = '', hideBlock = false } = $props();

// Transform URLs in features array from Builder.io
const transformedFeatures = $derived(
    features.map((feature) => {
        const linkTo = stripSiteDomain(feature.linkTo);

        return {
            ...feature,
            linkTo,
            linkAttributes: getLinkAttributes(linkTo),
        };
    }),
);
</script>

{#if !hideBlock}
    <div class="featured-items">
        <BlockHeader {header} {ctaTo} {ctaText} />

        <div class="featured-items__grid">
            {#each transformedFeatures as colItem, index (index)}
                <a href={colItem.linkTo} {...colItem.linkAttributes} class="featured-items__item">
                    {#if colItem.image?.src}
                        <img
                            src={formatBuilderImageUrlForAspectRatio(
                                colItem.image.src,
                                FEATURED_IMAGE_FALLBACK_WIDTH,
                                FEATURED_IMAGE_ASPECT_RATIO,
                                FEATURED_IMAGE_PARAMS,
                            )}
                            srcset={formatBuilderImageSrcsetForAspectRatio(
                                colItem.image.src,
                                FEATURED_IMAGE_WIDTHS,
                                FEATURED_IMAGE_ASPECT_RATIO,
                                FEATURED_IMAGE_PARAMS,
                            )}
                            sizes={FEATURED_IMAGE_SIZES}
                            alt={colItem.image.altText || colItem.image.alt || ''}
                            class="featured-items__image"
                            loading="lazy"
                            decoding="async"
                        />
                    {/if}
                    <div class="featured-items__text text">
                        {colItem.featuredText}
                    </div>
                </a>
            {/each}
        </div>
    </div>
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/typography';
    @use 'sass-mq/mq' as *;

    .featured-items {
        padding-left: var(--layout-pagegrid-margin);
        padding-right: var(--layout-pagegrid-margin);

        &__grid {
            display: grid;
            gap: var(--layout-pagegrid-gutter);
            grid-template-columns: 1fr;
            position: relative;

            @include mq($from: desktop) {
                grid-template-columns: repeat(2, 1fr);
            }
        }

        &__item {
            :global(picture) {
                display: block;
                overflow: hidden;
            }
            :global(img) {
                aspect-ratio: var(--layout-media-ar);
                object-fit: cover;
                transition: transform var(--timing-duration-long) !important;
                width: 100%;
            }
            &:hover {
                text-decoration: none;
                :global(img) {
                    transform: scale(1.1) !important;
                }
            }

        }

        &__text {
            @include typography.body-emphasis;
            color: var(--colour-dreamless-sleep);
            margin-top: 0.8rem;
            max-width: 42rem;
            text-decoration: none;
            text-transform: uppercase;

            @include mq($from: tablet) {
                margin-top: 1.6rem;
                padding: 0;
            }
        }
    }
</style>
